import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { useApiMutation } from '../../../../../../infra/api';
import { useRouteParams } from '../../../../../../infra/routing';
import { getActiveBrandAppIcon, useSession } from '../../../../../../infra/session';
import { endpoints } from '../../../../network';
import { useEditableField } from '../../../hooks/use-editable-field';
import { initializePushContent } from './initialize';
import { PushContent } from './push-content';
export var PushContentContainer = function () {
    var _a;
    var store = useStore();
    var session = useSession().data;
    var campaignId = useRouteParams().campaignId;
    var editorRef = useRef(null);
    var _b = useEditableField('pushBody'), pushContent = _b.field, setPushBodyValue = _b.setValue;
    var pushTitle = useEditableField('pushTitle').field;
    var pushBodyLimit = useEditableField('pushBodyLimit').field;
    // AI suggestions state
    var _c = useState([]), pushBodySuggestions = _c[0], setPushBodySuggestions = _c[1];
    // Helper function to check if rich text content is empty
    var isEmptyRichTextContent = function (content) {
        if (!content)
            return true;
        var trimmed = content.trim();
        return trimmed === '' || trimmed === '<p></p>' || /^<p[^>]*><\/p>$/.test(trimmed);
    };
    // Refine campaign text mutation
    var refineCampaignTextMutation = useApiMutation({
        endpoint: endpoints.refineCampaignTextMutation,
        options: {
            onError: function (error) {
                console.error('AI text processing failed:', error);
            },
        },
    });
    // Campaign suggestions mutation
    var campaignSuggestionsMutation = useApiMutation({
        endpoint: endpoints.campaignSuggestionsMutation,
        options: {
            onSuccess: function (response) {
                // Convert API response to format expected by AI popover
                var formattedSuggestions = [];
                if ('push_bodies' in response) {
                    // For body topics: use display_text as label, prompt for content generation
                    formattedSuggestions = response.push_bodies.map(function (pushBody) { return ({
                        label: pushBody,
                        prompt: pushBody, // This will be used for generate API, not refine
                    }); });
                }
                setPushBodySuggestions(formattedSuggestions);
            },
            onError: function (error) {
                console.error('Failed to fetch campaign suggestions:', error);
            },
        },
    });
    // Function to fetch suggestions for push body
    var fetchSuggestions = function () {
        var _a;
        if (!campaignId) {
            console.warn('Campaign ID not available for fetching suggestions');
            return;
        }
        // Only fetch from API if the field is empty
        if (isEmptyRichTextContent(pushContent.value)) {
            // Pass current field values as extraContext, excluding empty values
            var extraContext = {};
            // Only include non-empty values
            if ((_a = pushTitle.value) === null || _a === void 0 ? void 0 : _a.trim()) {
                extraContext.title = pushTitle.value.trim();
            }
            campaignSuggestionsMutation.mutate({
                campaignId: campaignId,
                data: {
                    kind: 'push_body',
                    extraContext: extraContext,
                },
            });
        }
        // If field has content, we don't fetch suggestions (will use hardcoded ones)
    };
    // Function to handle AI text refinement
    var handleRefineText = function (prompt, originalText, partialText) {
        var _a;
        if (!campaignId) {
            console.warn('Campaign ID not available for AI text refinement');
            return Promise.reject(new Error('Campaign ID not available'));
        }
        // Pass current field values as extraContext, excluding empty values
        var extraContext = {};
        // Only include non-empty values
        if ((_a = pushTitle.value) === null || _a === void 0 ? void 0 : _a.trim()) {
            extraContext.title = pushTitle.value.trim();
        }
        return refineCampaignTextMutation.mutateAsync({
            campaignId: campaignId,
            data: {
                section: 'push_body',
                prompt: prompt,
                original: originalText,
                partial: partialText,
                extraContext: extraContext,
            },
        });
    };
    // Determine which suggestions to use based on field content
    var getEffectiveSuggestions = function (apiSuggestions) {
        var currentFieldValue = pushContent.value || '';
        // If field is empty, use API suggestions; if has content, use empty array (will fall back to hardcoded)
        return isEmptyRichTextContent(currentFieldValue) ? apiSuggestions : [];
    };
    var logo = getActiveBrandAppIcon(session);
    var truncatedTitle = pushTitle.value.length > 40 ? "".concat(pushTitle.value.slice(0, 40), "...") : pushTitle.value;
    useEffect(function () {
        initializePushContent({ store: store });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return (_jsx(PushContent, { logoImage: logo, pushContent: pushContent.value, onPushBodyChange: setPushBodyValue, pushBodyError: (_a = pushContent.error) === null || _a === void 0 ? void 0 : _a.message, editorRef: editorRef, title: truncatedTitle, pushBodyLimit: pushBodyLimit.value, 
        // AI functionality props
        aiSuggestions: getEffectiveSuggestions(pushBodySuggestions), onFetchAiSuggestions: fetchSuggestions, onRefineText: handleRefineText, isRefining: refineCampaignTextMutation.isLoading, isFetchingSuggestions: campaignSuggestionsMutation.isLoading, suggestionsAreDirectContent: (pushContent.value || '').trim() === '' || pushContent.value === '<p></p>' }));
};
